import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { BackToTop, FileViewer } from '@intelishs-group/ui';
import { ShellSesion, type ShellMarca } from '@intelishs-group/layout';
import { NavegacionService } from '@services/navegacion.service';
import { AuthService } from '@auth';

/**
 * Identidad visual del shell. Es UNA sola marca: este sistema no resuelve
 * empresa por subdominio, asi que no hay `shared/branding/` ni peticion de
 * marca — y por eso la constante vive aqui, en la raiz de composicion, y no en
 * un servicio.
 *
 * El color NO se declara aqui: sale de los tokens del theme
 * (`--color-primary: #134477`), que ya es el azul de SINAY. Si algun dia este
 * sistema necesita un color propio y fijo, va como bloque de custom properties
 * en `src/styles.css` —que `angular.json` carga DESPUES del theme y gana por
 * cascada—, redefiniendo TODA la familia de tokens y no solo el primario.
 */
const MARCA: ShellMarca = { nombre: 'SINAY - Portal de Administración' };

/**
 * Raiz de composicion. Es el UNICO punto donde se juntan la sesion, el catalogo
 * de modulos y el shell; por eso no hay un componente contenedor intermedio,
 * que seria una capa sin contenido propio.
 */
@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ShellSesion, FileViewer, BackToTop],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly auth = inject(AuthService);
  protected readonly navegacion = inject(NavegacionService);

  protected readonly marca = MARCA;
  protected readonly autenticado = this.auth.autenticado;

  /**
   * El marco se monta cuando hay sesion.
   *
   * Antes esto ademas exigia NO estar en el portal, porque el portal —la
   * rejilla de accesos— vivia en la ruta `/login` de este mismo proyecto y el
   * menu lateral se dibujaba alrededor del propio selector.
   *
   * Ya no: el login y la rejilla son una aplicacion aparte a la que este portal
   * redirige, asi que aqui no queda ninguna pantalla de las de "hay sesion pero
   * todavia no estas dentro de nada". Si no hay sesion, `sesionGuard` saca al
   * usuario del origen antes de que haya nada que montar.
   */
  protected readonly mostrarShell = computed(() => this.autenticado());

  /*
   * ── AQUI VIVIAN ~50 LINEAS DE CABLEADO, Y SE FUERON A LA LIBRERIA ───────
   *
   * `accesos`, `accesoActual`, `cambiarAcceso()` y `salir()`: traducir la sesion
   * a las entradas del shell y manejar sus dos salidas. De esas cincuenta lineas
   * solo `marca` cambiaba de un front a otro, asi que los otros ocho repos
   * habrian tenido que copiarlas para migrar.
   *
   * Medirlas antes de copiarlas encontro DOS fallos que no dan ningun error:
   *
   *   · `cambiarAcceso()` navegaba sin llamar a `elegir-acceso`, que es lo que
   *     reemite la cookie. El acceso activo vive en el TOKEN, asi que la
   *     aplicacion de destino arrancaba con los modulos de ESTA.
   *   · `accesoActual` se deducia con `find(a => a.aplicacionCodigo === null)`
   *     —«el que no tiene aplicacion es el mio»—, y acertaba solo por ser el
   *     portal. Ahora sale de `accesoActivo`, que es lo que dice el servidor.
   *
   * Ver `ShellSesion` en `@intelishs-group/layout` (5.0.2).
   */

  constructor() {
    // El catalogo de modulos depende de si hay ACCESO TOTAL, pero
    // `NavegacionService` no debe conocer la autenticacion: si la inyectara, no
    // se podria probar ni reusar sin montar media app. La dependencia se
    // resuelve AQUI, en la raiz.
    //
    // Se manda el hecho (`esSuperadmin`) y no `roles`: el codigo de un rol solo
    // identifica dentro de su aplicacion, y compararlo contra un literal es lo
    // que dejo el riel con una sola entrada al migrar a la sesion de
    // plataforma. Ver `ModuloNav.soloSuperadmin`.
    effect(() => {
      const sesion = this.auth.sesion();
      this.navegacion.establecerSuperadmin(sesion ? sesion.esSuperadmin : null);
    });
  }
}
